import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCountryLabel } from 'hooks/useCountryLabel';
import routes from 'routes';
import { updateDetailsRequest } from 'store/account/actions';
import { getAuthUser } from 'store/auth/selectors';
import { User } from 'store/auth/models';
import {
  getCommonTimezones,
  getCountriesExternalData,
} from 'store/common/selectors';
import {
  fetchCountriesExternalDataRequest,
  fetchTimezonesRequest,
} from 'store/common/actions';
import { PwaIcon } from './Icon';
import { PwaField, PwaSection, PwaTopBar } from './ui';

function phoneCodeFromCountry(country: {
  idd?: { root?: string; suffixes?: string[] };
}): string {
  if (!country?.idd) return '';
  if (
    country.idd.root === '' &&
    (!country.idd.suffixes || country.idd.suffixes.length === 0)
  ) {
    return '+672';
  }
  return `${country.idd.root || ''}${country.idd.suffixes?.[0] || ''}`;
}

export const PwaPersonalDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { localizeCountry } = useCountryLabel();
  const user = useSelector(getAuthUser);
  const timezones = useSelector(getCommonTimezones);
  const countriesExternalData = useSelector(getCountriesExternalData);

  const [firstName, setFirstName] = React.useState(user?.firstName ?? '');
  const [lastName, setLastName] = React.useState(user?.lastName ?? '');
  const [companyName, setCompanyName] = React.useState(user?.companyName ?? '');
  const [phonePrefix, setPhonePrefix] = React.useState(user?.phonePrefix ?? '');
  const [phoneNumber, setPhoneNumber] = React.useState(user?.phoneNumber ?? '');
  const [email, setEmail] = React.useState(user?.email ?? '');
  const [website, setWebsite] = React.useState(user?.website ?? '');
  const [timezone, setTimezone] = React.useState(user?.timezone ?? '');

  React.useEffect(() => {
    if (!timezones?.length) dispatch(fetchTimezonesRequest());
    if (!countriesExternalData?.length) {
      dispatch(fetchCountriesExternalDataRequest());
    }
  }, [dispatch, timezones?.length, countriesExternalData?.length]);

  React.useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setCompanyName(user.companyName ?? '');
    setPhonePrefix(user.phonePrefix ?? '');
    setPhoneNumber(user.phoneNumber ?? '');
    setEmail(user.email ?? '');
    setWebsite(user.website ?? '');
    setTimezone(user.timezone ?? '');
  }, [user]);

  const goBack = () => navigate(routes.account.root);

  const onSave = () => {
    if (!user) return;
    const next: User = {
      ...user,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      companyName: companyName.trim(),
      phonePrefix,
      phoneNumber: phoneNumber.trim(),
      email: email.trim(),
      website: website.trim(),
      timezone,
    };
    dispatch(updateDetailsRequest(next));
    goBack();
  };

  const phoneCodeOptions = React.useMemo(() => {
    const opts = (countriesExternalData || [])
      .map((c) => {
        const code = phoneCodeFromCountry(c).trim();
        if (!code) return null;
        const rawName = c.name?.common?.includes(',')
          ? c.name.common.split(',')[0]
          : c.name?.common || '';
        const countryName = localizeCountry(c.cca2, rawName) || rawName;
        return {
          value: code,
          label: countryName || code,
          detail: countryName ? code : undefined,
          flag: {
            src: c.flags?.png,
            alt: c.flags?.alt || countryName || code,
            cca2: c.cca2 || '',
          },
          sortKey: (countryName || code).toLowerCase(),
        };
      })
      .filter((opt): opt is NonNullable<typeof opt> => Boolean(opt));

    opts.sort((a, b) => a.sortKey.localeCompare(b.sortKey, i18n.language));

    if (phonePrefix && !opts.some((opt) => opt.value === phonePrefix)) {
      opts.unshift({
        value: phonePrefix,
        label: phonePrefix,
        detail: undefined,
        flag: undefined,
        sortKey: phonePrefix.toLowerCase(),
      });
    }

    return opts.map(({ value, label, detail, flag }) => ({
      value,
      label,
      detail,
      flag,
    }));
  }, [countriesExternalData, phonePrefix, localizeCountry, i18n.language]);

  const timezoneOptions = React.useMemo(() => {
    const opts = (timezones || []).map((tz) => ({
      value: tz.displayTimeZoneName,
      label: tz.displayName || tz.displayTimeZoneName,
    }));
    if (timezone && !opts.some((o) => o.value === timezone)) {
      opts.unshift({ value: timezone, label: timezone });
    }
    return opts;
  }, [timezones, timezone]);

  return (
    <div className="pwa-more-page" data-shell="pwa">
      <PwaTopBar
        title={t('users.personal-details', { defaultValue: 'Personal details' })}
        onBack={goBack}
        backLabel={t('common.back')}
      />
      <main className="pwa-main has-actionbar">
        <div style={{ height: 16 }} />
        <PwaSection title={t('users.name', { defaultValue: 'Name' })}>
          <div className="pwa-card pad">
            <div className="pwa-fields">
              <PwaField
                id="ed-first"
                label={t('users.pwa-first-name', { defaultValue: 'First name' })}
                required
                value={firstName}
                onChange={setFirstName}
              />
              <PwaField
                id="ed-last"
                label={t('users.pwa-last-name', { defaultValue: 'Last name' })}
                required
                value={lastName}
                onChange={setLastName}
              />
              <PwaField
                id="ed-company"
                label={t('users.pwa-company', { defaultValue: 'Company' })}
                required
                value={companyName}
                onChange={setCompanyName}
                full
              />
            </div>
          </div>
        </PwaSection>
        <PwaSection title={t('users.contact', { defaultValue: 'Contact' })}>
          <div className="pwa-card pad">
            <div
              className="pwa-fields"
              style={{ gridTemplateColumns: '108px 1fr' }}
            >
              <PwaField
                id="ed-code"
                label={t('signup.code')}
                required
                value={phonePrefix}
                onChange={setPhonePrefix}
                sheetSelect
                options={phoneCodeOptions}
                triggerLabel={phonePrefix}
                searchPlaceholder={t('common.search', {
                  defaultValue: 'Search',
                })}
                emptyLabel={t('common.not-set', { defaultValue: 'Not set' })}
              />
              <PwaField
                id="ed-phone"
                label={t('signup.phone-number', { defaultValue: 'Phone' })}
                required
                value={phoneNumber}
                onChange={setPhoneNumber}
                type="tel"
              />
              <PwaField
                id="ed-email"
                label={t('common.email', { defaultValue: 'Email' })}
                required
                value={email}
                onChange={setEmail}
                type="email"
                full
              />
              <PwaField
                id="ed-web"
                label={t('common.website', { defaultValue: 'Website' })}
                value={website}
                onChange={setWebsite}
                full
              />
            </div>
          </div>
        </PwaSection>
        <PwaSection title={t('users.region', { defaultValue: 'Region' })}>
          <div className="pwa-card pad">
            <div className="pwa-fields">
              <PwaField
                id="ed-tz"
                label={t('common.timezone', { defaultValue: 'Time zone' })}
                required
                value={timezone}
                onChange={setTimezone}
                sheetSelect
                options={timezoneOptions}
                searchPlaceholder={t('common.search', {
                  defaultValue: 'Search',
                })}
                emptyLabel={t('common.not-set', { defaultValue: 'Not set' })}
                full
              />
            </div>
          </div>
        </PwaSection>
      </main>
      <div className="pwa-actionbar" data-shell="pwa">
        <button type="button" className="pwa-btn secondary" onClick={goBack}>
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </button>
        <button type="button" className="pwa-btn primary" onClick={onSave}>
          <PwaIcon name="check" size={18} />
          {t('users.pwa-save-changes', { defaultValue: 'Save changes' })}
        </button>
      </div>
    </div>
  );
};

export default PwaPersonalDetailsPage;
